import { Component, ReactNode } from 'react'
import * as Sentry from '@sentry/nextjs'

import { isChunkLoadError } from '@/lib/chunkLoadError'
import {
  recoverFromChunkError,
  reloadCurrentPage,
  type ChunkRecoveryOutcome,
} from '@/lib/chunkRecovery'

interface Props {
  children?: ReactNode
}

interface State {
  hasError: boolean
  isChunkError: boolean
  chunkOutcome: ChunkRecoveryOutcome | null
}

// Shared so the 500 fallback button and the chunk-refresh button never drift apart.
const ACTION_BUTTON_CLASS =
  'rounded-md bg-blue-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'

// This boundary wraps the whole app from _app, and nothing clears `hasError` —
// so a client-side <Link> kept re-rendering this same 500 screen and the user
// could never leave it (Sentry LINQ-ALPHA-CLIENT-D9: 11 crashes, no recovery).
// A same-origin hard navigation rebuilds the app with a fresh boundary.
// Clearing the flag in place would not work: the content that threw is usually
// persisted, so the children would throw again on the very next render.
const goHome = () => window.location.assign('/analytics')

class GlobalError extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    // Define a state variable to track whether is an error or not
    this.state = { hasError: false, isChunkError: false, chunkOutcome: null }
  }
  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI. Flag chunk
    // errors so render() shows a refresh splash instead of the generic 500
    // (and never re-renders the crashed children, which would re-throw). The
    // recovery outcome is filled in by componentDidCatch.
    return { hasError: true, isChunkError: isChunkLoadError(error), chunkOutcome: null }
  }
  componentDidCatch(error: Error): void {
    // A next/dynamic chunk failing to load surfaces here as a render error.
    // That's usually expected deploy-skew (a stale buildId requesting a gone
    // chunk), not a real crash: recover by hard-reloading to fresh assets
    // instead of showing the 500 fallback.
    if (isChunkLoadError(error)) {
      // immediate: the boundary has already unmounted the subtree (toast
      // container included), so reload rather than route through a toast.
      const outcome = recoverFromChunkError(error, { immediate: true })
      this.setState({ chunkOutcome: outcome })
      // 'exhausted' = a reload was already attempted for this URL and the chunk
      // is STILL missing → this is a genuinely broken deploy / missing asset,
      // not transient skew, so surface it (tagged, to pass beforeSend's
      // chunk-error drop). 'offline' is the user's network, not our deploy —
      // don't report it.
      if (outcome === 'exhausted') {
        Sentry.captureException(error, { tags: { chunk_load_recovery: 'exhausted' } })
      }
      return
    }
    Sentry.captureException(error)
  }
  render() {
    const { hasError, isChunkError, chunkOutcome } = this.state

    if (hasError && isChunkError) {
      const offline = chunkOutcome === 'offline'
      // 'reloading' | 'toast' | null (first frame, before componentDidCatch) →
      // a hard reload is (or is about to be) in flight. Otherwise the auto
      // reload was guarded off and the button below is the only recovery, so
      // don't promise a refresh that isn't happening.
      const reloadInFlight =
        chunkOutcome === 'reloading' || chunkOutcome === 'toast' || chunkOutcome === null

      const heading = offline ? 'You appear to be offline' : 'A new version is available'
      const body = offline
        ? 'Reconnect and refresh to load the latest version.'
        : reloadInFlight
        ? 'Refreshing to load the latest version…'
        : 'Please refresh to load the latest version.'

      return (
        <main className="grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8 h-screen">
          <div className="text-center">
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              {heading}
            </h1>
            <p className="mt-6 text-base leading-7 text-gray-600">{body}</p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              {/* Reload through the same guard bookkeeping as auto-recovery. */}
              <button type="button" onClick={reloadCurrentPage} className={ACTION_BUTTON_CLASS}>
                Refresh
              </button>
            </div>
          </div>
        </main>
      )
    }
    // Check if the error is thrown
    if (hasError) {
      // You can render any custom fallback UI
      return (
        <>
          <main className="grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8 h-screen">
            <div className="text-center">
              <p className="text-base font-semibold text-blue-500">500</p>
              <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                Oops! Something went wrong
              </h1>
              <p className="mt-6 text-base leading-7 text-gray-600">
                Sorry, the server is having some issues. Please try again later.
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <button type="button" onClick={goHome} className={ACTION_BUTTON_CLASS}>
                  Go back home
                </button>
              </div>
            </div>
          </main>
        </>
      )
    }

    // Return children components in case of no error

    return this.props.children
  }
}

export default GlobalError
